<template>
  <div>
    <div v-if="!printMode">
      <h6>Controle Mensal</h6>
      <p></p>

      <div class="row mb-3">
        <div class="col-auto">
          <label for="mes_ref">Mês de referência:</label>
          <input
            id="mes_ref"
            type="month"
            class="form-control"
            v-model="mesAnoLocal"
          />
        </div>
        <div class="col-auto align-self-end">
          <button class="btn btn-primary" @click="abrirPrint">
            Executar
          </button>
        </div>
      </div>
    </div>

    <div v-else class="relatorio-body" :data-user="userName">
      <div v-if="loading">Carregando…</div>

      <div v-else-if="metrics">
        <p class="text-center" style="font-size: 14px;">
          Período: {{ nomeMes }} / {{ ano }}
        </p>

        <p
          v-if="periodoTexto"
          class="text-center text-muted"
          style="font-size: 11px; font-style: italic; margin-top: -8px;"
        >
          {{ periodoTexto }}
        </p>

        <!-- NOVO BLOCO -->
        <h6>Resumo da Base de Alunos</h6>
        <table class="table table-bordered table-sm" style="font-size: 11px;">
          <thead>
            <tr>
              <th>Fechamento anterior</th>
              <th>Entraram no período</th>
              <th>Saíram no período</th>
              <th>Saldo líquido</th>
              <th>Alunos na data de corte</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{{ metrics.alunos_fechamento_anterior }}</td>
              <td>{{ metrics.entradas_base }}</td>
              <td>{{ metrics.saidas_base }}</td>
              <td>{{ formatarInteiroComSinal(metrics.saldo_liquido_base) }}</td>
              <td>{{ metrics.total_alunos }}</td>
            </tr>
          </tbody>
        </table>

        <p style="font-size: 11px; font-style: italic;">
          Fórmula: alunos na data de corte = fechamento anterior + entradas - saídas.
        </p>

        <div class="row">
          <div class="col-md-4">
            <h6>Entraram no período</h6>
            <table class="table table-bordered table-sm" style="font-size: 11px;">
              <tbody>
                <tr>
                  <th>Novas matrículas</th>
                  <td>{{ metrics.matriculas_novas }}</td>
                </tr>
                <tr>
                  <th>Retornos</th>
                  <td>{{ metrics.retornos }}</td>
                </tr>
                <tr>
                  <th>Total de entradas</th>
                  <td>{{ metrics.entradas_base }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="col-md-4">
            <h6>Saíram no período</h6>
            <table class="table table-bordered table-sm" style="font-size: 11px;">
              <tbody>
                <tr>
                  <th>Cancelamentos</th>
                  <td>{{ metrics.cancelamentos_base }}</td>
                </tr>
                <tr>
                  <th>Vencimentos não renovados</th>
                  <td>{{ metrics.vencimentos_nao_renovados }}</td>
                </tr>
                <tr>
                  <th>Total de saídas</th>
                  <td>{{ metrics.saidas_base }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="col-md-4">
            <h6>Manutenção da base</h6>
            <table class="table table-bordered table-sm" style="font-size: 11px;">
              <tbody>
                <tr>
                  <th>Renovações registradas</th>
                  <td>{{ metrics.renovacoes_total }}</td>
                </tr>
                <tr>
                  <th>Renovações antecipadas</th>
                  <td>{{ metrics.renovacoes_antecipadas }}</td>
                </tr>
                <tr>
                  <th>Planos vencendo</th>
                  <td>{{ metrics.planos_vencendo }}</td>
                </tr>
                <tr>
                  <th>% Renovação</th>
                  <td>{{ formatarNumero(metrics.percentual_renovacao) }}%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>


        <p style="font-size: 11px; font-style: italic;">
          *Alunos: total de alunos ativos na data de corte do relatório.
        </p>

        <p style="font-size: 11px; font-style: italic;">
          % Renovação: percentual das renovações registradas no período em relação ao total de matrículas com vencimento previsto no período apurado.
        </p>

        <div class="mt-3 chart-container">
          <Pie v-if="chartData0" :data="chartData0" :options="chartOptions0" />
        </div>

        <div class="mt-3 chart-container">
          <Pie v-if="chartData1" :data="chartData1" :options="chartOptions1" />
        </div>

        <div class="mt-3 chart-container">
          <Pie v-if="chartData2" :data="chartData2" :options="chartOptions2" />
        </div>

        <p style="font-size: 10px; font-style: italic; margin-top: 8px;">
          Gráfico 1: composição da base operacional no período apurado, separando alunos da base, retornos e novas matrículas.<br>
          Gráfico 2: distribuição dos movimentos operacionais do período: novas matrículas, retornos, renovações registradas e cancelamentos.<br>
          Gráfico 3: matrículas com previsão de encerramento no período em comparação com as renovações registradas no período.
        </p>
      </div>

      <div v-else>
        <p class="text-muted">Nenhum dado encontrado para o período selecionado.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axios from '@/services/axios'

import { Pie } from 'vue-chartjs'
import {
  Chart as ChartJS,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'

ChartJS.register(ArcElement, Title, Tooltip, Legend)

const props = defineProps({
  printMode: { type: Boolean, default: false },
  printTitle: { type: String, default: 'Relatório' },
  userName: { type: String, default: '' },
})

const router = useRouter()
const route = useRoute()

const mesAnoLocal = ref('')
const loading = ref(false)
const ano = ref(null)
const mes = ref(null)
const metrics = ref(null)
const periodo = ref(null)

const mesesNomes = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
]

const nomeMes = computed(() => {
  if (!mes.value) return ''
  return mesesNomes[mes.value - 1] || ''
})

function formatarNumero(valor) {
  if (valor == null || isNaN(valor)) return '0,00'
  return Number(valor).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function formatarInteiroComSinal(valor) {
  const numero = Number(valor || 0)
  if (numero > 0) return `+${numero}`
  if (numero < 0) return `${numero}`
  return '0'
}

function formatarDataBR(valor) {
  if (!valor) return ''

  const data = valor instanceof Date ? valor : new Date(valor)

  if (Number.isNaN(data.getTime())) return ''

  return data.toLocaleDateString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
  })
}

const periodoTexto = computed(() => {
  if (!periodo.value?.inicio || !periodo.value?.fim) return ''

  const inicio = formatarDataBR(periodo.value.inicio)
  const fim = formatarDataBR(periodo.value.fim)
  const fechamentoAnterior = formatarDataBR(periodo.value.fechamento_anterior)

  if (periodo.value.parcial) {
    return `Para o mês atual, os dados são acumulados até a data de execução do relatório (${fim}). Fechamento anterior: ${fechamentoAnterior}. Período apurado: ${inicio} a ${fim}.`
  }

  return `Fechamento anterior: ${fechamentoAnterior}. Período apurado: ${inicio} a ${fim}.`
})

async function carregarDados(anoParam, mesParam) {
  loading.value = true
  try {
    const res = await axios.get('/gestao/relatorios/operacionais/controle-mensal', {
      params: { ano: anoParam, mes: mesParam },
    })

    ano.value = res.data.ano
    mes.value = res.data.mes
    periodo.value = res.data.periodo || null
    metrics.value = res.data.metrics || null
  } catch (e) {
    console.error('Erro em ControleMensal (print):', e)
    periodo.value = null
    metrics.value = null
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  if (props.printMode) {
    const anoQ = parseInt(route.query.ano, 10) || new Date().getFullYear()
    const mesQ = parseInt(route.query.mes, 10) || (new Date().getMonth() + 1)
    carregarDados(anoQ, mesQ)
  }
})

function abrirPrint() {
  if (!mesAnoLocal.value) return

  const [anoStr, mesStr] = mesAnoLocal.value.split('-')
  const anoNum = parseInt(anoStr, 10)
  const mesNum = parseInt(mesStr, 10)

  const mesNome = mesesNomes[mesNum - 1] || ''
  const subtitle = `Período: ${mesNome} / ${anoNum}`

  router.push({
    path: '/gestao/relatorios/print/controle-mensal',
    query: {
      title: 'CONTROLE MENSAL',
      subtitle,
      ano: anoNum,
      mes: mesNum,
    },
  })
}

const chartData0 = computed(() => {
  if (!metrics.value) return null
  const m = metrics.value

  const novos = m.matriculas_novas || 0
  const retornos = m.retornos || 0
  const alunosBase = Math.max((m.total_alunos || 0) - (novos + retornos), 0)

  return {
    labels: ['Alunos (base)', 'Retornos', 'Novos'],
    datasets: [
      {
        data: [alunosBase, retornos, novos],
        backgroundColor: [
          'rgba(54, 162, 235, 0.7)',
          'rgba(255, 159, 64, 0.7)',
          'rgba(75, 192, 192, 0.7)',
        ],
      },
    ],
  }
})

const chartOptions0 = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom' },
    title: { display: true, text: 'Composição da base operacional' },
    tooltip: {
      callbacks: {
        label: (ctx) => `${ctx.label}: ${ctx.raw || 0}`,
      },
    },
  },
}

const chartData1 = computed(() => {
  if (!metrics.value) return null
  const m = metrics.value

  const novas = m.matriculas_novas || 0
  const retornos = m.retornos || 0
  const renovados = m.renovacoes_total || 0
  const cancelados = m.cancelamentos || 0

  if (novas === 0 && retornos === 0 && renovados === 0 && cancelados === 0) {
    return {
      labels: ['Sem dados'],
      datasets: [
        {
          data: [1],
          backgroundColor: ['rgba(200, 200, 200, 0.7)'],
        },
      ],
    }
  }

  return {
    labels: ['Novas matrículas', 'Retornos', 'Renovações registradas', 'Cancelamentos'],
    datasets: [
      {
        data: [novas, retornos, renovados, cancelados],
        backgroundColor: [
          'rgba(75, 192, 192, 0.7)',
          'rgba(255, 159, 64, 0.7)',
          'rgba(54, 162, 235, 0.7)',
          'rgba(255, 99, 132, 0.7)',
        ],
      },
    ],
  }
})

const chartOptions1 = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom' },
    title: { display: true, text: 'Movimentação operacional do período' },
    tooltip: {
      callbacks: {
        label: (ctx) => {
          const label = ctx.label || ''
          const value = ctx.raw || 0
          return `${label}: ${value}`
        },
      },
    },
  },
}

const chartData2 = computed(() => {
  if (!metrics.value) return null
  const m = metrics.value

  const renovados = m.renovacoes_total || 0
  const vencendo = m.planos_vencendo || 0
  const restante = Math.max(vencendo - renovados, 0)

  if (renovados === 0 && restante === 0) {
    return {
      labels: ['Sem dados'],
      datasets: [
        {
          data: [1],
          backgroundColor: ['rgba(200, 200, 200, 0.7)'],
        },
      ],
    }
  }

  return {
    labels: ['Renovações registradas', 'Demais vencimentos do período'],
    datasets: [
      {
        data: [renovados, restante],
        backgroundColor: [
          'rgba(75, 192, 192, 0.7)',
          'rgba(201, 203, 207, 0.7)',
        ],
      },
    ],
  }
})

const chartOptions2 = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom' },
    title: { display: true, text: 'Planos vencendo no período x Renovações registradas' },
    tooltip: {
      callbacks: {
        label: (ctx) => {
          const label = ctx.label || ''
          const value = ctx.raw || 0
          return `${label}: ${value}`
        },
      },
    },
  },
}

const userName = computed(() => props.userName || '')
</script>

<style scoped>
.mb-3 {
  margin-bottom: 1rem;
}
.mt-3 {
  margin-top: 1rem;
}
.text-muted {
  font-size: 12px;
}
.chart-container {
  width: 100%;
  height: 260px;
}
</style>